import { HttpClient, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Namespace } from 'enums-lib';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IAuthService, IEnvironment, IRuntimeEnvironment, RequestConfigProperties } from 'interfaces-lib';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { Identity, IdentityStorage } from 'storage-service-lib';
import { currentUser } from './current-user';

export const RETURN_URL_STORAGE_KEY = 'kp-auth-returnurl';
export const CALLBACK_NAMESPACE_STORAGE_KEY = 'kp-auth-callback-namespace';

export function getCallbackNamespace(callbackUrl: string): Namespace | null {
  const instanceId = new URL(callbackUrl).searchParams.get('instance') ?? '';
  const key = `${CALLBACK_NAMESPACE_STORAGE_KEY}-${instanceId}`;
  const namespace = sessionStorage.getItem(key) as Namespace | null;
  sessionStorage.removeItem(key);
  return namespace;
}

export abstract class AbstractAuthService implements IAuthService {
  protected readonly instanceId: string = crypto.randomUUID();
  private queue: Subject<string> = new Subject<string>();
  private getTokenInProgress = false;
  private postTokenActionCalled = false;
  private queue$: Observable<string> = this.queue.asObservable();
  private readonly featureFlagService = inject(FeatureFlagService);
  protected readonly environment = inject<Partial<IEnvironment>>(ENVIRONMENT);
  protected readonly runtimeEnvironment = inject<IRuntimeEnvironment | undefined>(RUNTIME_ENVIRONMENT);

  protected constructor(
    protected identityStorage: IdentityStorage,
    private http: HttpClient,
    private serviceProvider: SharedServiceProviderServiceFactory
  ) {
    console.log('AbstractAuthService created');
  }

  public abstract getRequestProperties(req: HttpRequest<any>, token: string): RequestConfigProperties | undefined;
  protected abstract getNamespace(): Namespace;
  protected abstract getRequestAuthenticationHostUrl(): string;
  protected postTokenAction(jwt: string): void {}

  public finishAuthFlow(responseUrl: string): string {
    const url = new URL(responseUrl);
    const instanceId = url.searchParams.get('instance') ?? '';
    const returnUrl = this.getReturnUrl(instanceId);
    const token = url.searchParams.get('token');

    if (!token) {
      console.error('AbstractAuthService.finishAuthFlow: no token found in response URL');
      throw new Error('No token found in authentication response');
    }

    const identity = this.getOrCreateIdentity();
    identity.token = token;
    this.identityStorage.put(identity);

    return returnUrl;
  }

  public getToken(): Observable<string> {
    let identity = this.identityStorage.get();
    if (!identity) {
      identity = { namespace: this.getNamespace(), tenant: '', token: '' };
      this.identityStorage.put(identity);
    }

    if (this.isTokenValid(identity.token)) {
      return this.runPostTokenActionOnce(identity.token).pipe(map(() => identity.token));
    }

    if (this.getTokenInProgress) {
      return this.queue$;
    }

    this.getTokenInProgress = true;
    const authHostUrl = this.getRequestAuthenticationHostUrl();

    return this.http.get(authHostUrl, { responseType: 'text' }).pipe(
      tap((authUrl: string) => this.redirectToAuthProvider(authUrl)),
      switchMap(() => this.queue$),
      catchError((err) => {
        console.error('AbstractAuthService.getToken: error fetching auth URL from', authHostUrl, err);
        this.getTokenInProgress = false;
        return throwError(() => err);
      }),
    );
  }

  public isTokenValid(token: string): boolean {
    if (!token) {
      return false;
    }
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.exp !== undefined && !this.hasExpired(decoded.exp);
  }

  public getTenantId(token: string): string {
    const decoded = jwtDecode<any>(token);
    return decoded['tenantId'];
  }

  private getOrCreateIdentity(): Identity {
    return this.identityStorage.get() ?? { namespace: this.getNamespace(), tenant: '', token: '' };
  }

  protected resolveWithToken(token: string): void {
    this.getTokenInProgress = false;
    // Emit the token only after the post-token action has run and the feature flags have been initialized.
    this.runPostTokenActionOnce(token).subscribe(() => this.queue.next(token));
  }

  /**
   * Runs the once-only post-token action and initializes the feature flags. The returned observable completes once the
   * flags have been loaded (or have failed to load - errors are swallowed by the feature flag service), so callers can
   * defer proceeding until the flags are available. On subsequent calls it completes immediately without re-running.
   */
  private runPostTokenActionOnce(token: string): Observable<void> {
    if (this.postTokenActionCalled) {
      return of(undefined);
    }
    this.postTokenActionCalled = true;
    this.postTokenAction(token);
    return this.initFeatureFlags(token);
  }

  private initFeatureFlags(token: string): Observable<void> {
    return this.featureFlagService.init({
      environment: this.environment.name ?? '',
      tenant_id: this.serviceProvider.getServiceProviderService(this.identityStorage.get()?.namespace).getAuthService().getTenantId(token) ?? '',
      user_id: currentUser.userInfo?.id.toString(),
      email: currentUser.userInfo?.email,
    });
  }

  protected redirectToAuthProvider(authUrl: string): void {
    const returnUrl = window.location.href;
    sessionStorage.setItem(`${RETURN_URL_STORAGE_KEY}-${this.instanceId}`, returnUrl);
    sessionStorage.setItem(`${CALLBACK_NAMESPACE_STORAGE_KEY}-${this.instanceId}`, String(this.getNamespace()));
    window.location.href = authUrl;
  }

  private getReturnUrl(instanceId: string): string {
    const storageKey = `${RETURN_URL_STORAGE_KEY}-${instanceId}`;
    const returnUrl = sessionStorage.getItem(storageKey) ?? window.location.origin;
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(`${CALLBACK_NAMESPACE_STORAGE_KEY}-${instanceId}`);
    return returnUrl;
  }

  private hasExpired(dateTimeInSecondsSinceEpoch: number): boolean {
    return Date.now() >= dateTimeInSecondsSinceEpoch * 1000;
  }
}
